import React, { useEffect, useState } from "react";
import {
  Typography,
  Box,
  List,
  ListItem,
  ListItemContent,
  ListDivider,
  Sheet,
  Table,
  iconButtonClasses,
  Button,
  IconButton,
  Card,
  Input,
  ButtonGroup,
} from "@mui/joy";
import { toast } from "react-toastify";
import { LoadingView, PageTitle, DatePickerModal, formatRangeLabel, StatusBadge } from "../../../components";
import { formatDate, thousandSeparator } from "../../../utils";
import SearchIcon from "@mui/icons-material/Search";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import {
  RefreshOutlined,
  RemoveRedEyeOutlined,
  WarningRounded,
} from "@mui/icons-material";
import { transactionsRequest, transactionsReset } from "../../../store/actions";
import { connect, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useMediaQuery } from "@mui/material";
import { STATUS, getStatusColor, getStatusLabel } from "../../../constant";
import { NAVIGATE_TO_TRANSACTIONDETAILSPAGE } from "../../../route/types";

const TRANS_TYPE_LABEL = {
  1: "Deposit",
  2: "Withdrawal",
  3: "Top Up",
};

const MobileViewTable = ({ data, props }) => {
  return (
    <Box sx={{ display: { xs: "block", md: "none" } }}>
      {data.map((listItem, index) => (
        <List key={index} size="sm" sx={{ "--ListItem-paddingX": 0 }}>
          <ListItem
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "start",
            }}
          >
            <ListItemContent
              sx={{ display: "flex", gap: 2, alignItems: "start" }}
            >
              <div>
                <Typography fontWeight={600} gutterBottom>
                  {thousandSeparator(listItem.amount)}{" "}
                  {listItem.currency}
                </Typography>
                <Typography level="body-xs" gutterBottom>
                  <b>Source: </b>{" "}
                  {listItem.source_account_name || listItem.source_account}
                </Typography>
                <Typography level="body-xs" gutterBottom>
                  <b>Trans Type: </b> {TRANS_TYPE_LABEL[listItem.trans_type]}
                </Typography>
                <Typography level="body-xs" gutterBottom>
                  <b>Agent Code: </b> {listItem.agent_code}
                </Typography>
              </div>
            </ListItemContent>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "flex-end",
                rowGap: 1,
              }}
            >
              <StatusBadge color={getStatusColor(listItem.status)}>
                {getStatusLabel(listItem.status)}
              </StatusBadge>
              <ButtonGroup variant="soft" size="sm">
                <Button color="primary" onClick={() => props.view(listItem)}>
                  <RemoveRedEyeOutlined />
                </Button>
              </ButtonGroup>
            </Box>
          </ListItem>
          <ListDivider />
        </List>
      ))}
    </Box>
  );
};

const DesktopViewTable = ({ data, props }) => {
  return (
    <Sheet
      variant="outlined"
      sx={{
        display: { xs: "none", md: "flex" },
        borderRadius: "sm",
        flexShrink: 1,
        overflow: "auto",
        minHeight: 0,
      }}
    >
      <Table
        aria-labelledby="tableTitle"
        stickyHeader
        hoverRow
        sx={{
          "--TableCell-headBackground": "var(--joy-palette-background-level1)",
          "--Table-headerUnderlineThickness": "1px",
          "--TableRow-hoverBackground": "var(--joy-palette-background-level1)",
          "--TableCell-paddingY": "4px",
          "--TableCell-paddingX": "8px",
          "& tr > *:last-child": {
            position: "sticky",
            right: 0,
            bgcolor: "var(--TableCell-headBackground)",
          },
        }}
      >
        <thead>
          <tr>
            <th style={{ width: 80, padding: "10px 6px" }}>Amount</th>
            <th style={{ width: 100, padding: "10px 6px" }}>Agent Code</th>
            <th style={{ width: 120, padding: "10px 6px" }}>Agent Name</th>
            <th style={{ width: 130, padding: "10px 6px" }}>Source Account</th>
            <th style={{ width: 80, padding: "10px 6px" }}>Status</th>
            <th style={{ width: 80, padding: "10px 6px" }}>Trans Type</th>
            <th aria-label="last" style={{ width: 50, padding: "10px 6px" }} />
          </tr>
        </thead>
        <tbody>
          {data.map((row, index) => (
            <tr key={index}>
              <td>
                <Typography level="body-sm">
                  {thousandSeparator(row.amount)} {row.currency}
                </Typography>
              </td>
              <td>
                <Typography level="body-sm">{row.agent_code}</Typography>
              </td>
              <td>
                <Typography level="body-sm">{row.agent_name}</Typography>
              </td>
              <td>
                <Typography level="body-sm" sx={{ wordBreak: "break-word" }}>
                  {row.source_account_name || row.source_account}
                </Typography>
              </td>
              <td>
                <StatusBadge color={getStatusColor(row.status)}>
                  {getStatusLabel(row.status)}
                </StatusBadge>
              </td>
              <td>
                <Typography level="body-sm">
                  {TRANS_TYPE_LABEL[row.trans_type]}
                </Typography>
              </td>
              <td>
                <ButtonGroup variant="soft" size="sm">
                  <Button
                    title="View"
                    color="primary"
                    onClick={() => props.view(row)}
                  >
                    <RemoveRedEyeOutlined />
                  </Button>
                </ButtonGroup>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </Sheet>
  );
};

const TransactionPage = ({
  loginResult,
  transactionsStatus,
  transactionsResult,
  transactionsErrorMessage,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isDesktop = useMediaQuery("(min-width:600px)");

  const [search, setSearch] = useState("");
  const [transType, setTransType] = useState("deposits");
  const [currentPage, setCurrentPage] = useState(0);
  const ITEMS_PER_PAGE = 20;

  useEffect(() => {
    dispatch(transactionsRequest(loginResult.token));
  }, []);

  useEffect(() => {
    if (transactionsStatus === STATUS.ERROR) {
      toast.error(transactionsErrorMessage);
      dispatch(transactionsReset());
    }
  }, [transactionsStatus, transactionsErrorMessage, dispatch]);

  const transData = transactionsResult?.[transType] || [];

  const filteredData = transData.filter((item) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      item.trans_date?.includes(search) ||
      item.source_account_name?.toLowerCase().includes(q) ||
      item.source_account?.toLowerCase().includes(q) ||
      item.destination_account?.toLowerCase().includes(q) ||
      item.agent_code?.toLowerCase().includes(q) ||
      item.agent_name?.toLowerCase().includes(q) ||
      item.op_reference?.toLowerCase().includes(q) ||
      item.uuid?.toLowerCase().includes(q) ||
      item.transaction_reference?.toLowerCase().includes(q) ||
      item.description?.toLowerCase().includes(q) ||
      item.amount?.toString().includes(search) ||
      TRANS_TYPE_LABEL[item.trans_type]?.toLowerCase().includes(q) ||
      getStatusLabel(item.status)?.toLowerCase().includes(q)
    );
  });

  const pageLength = Math.ceil(filteredData.length / ITEMS_PER_PAGE);

  const paginatedData = filteredData.slice(
    currentPage * ITEMS_PER_PAGE,
    (currentPage + 1) * ITEMS_PER_PAGE,
  );

  const viewTransactionDetails = (details) => {
    navigate(NAVIGATE_TO_TRANSACTIONDETAILSPAGE, {
      state: { transDetails: details },
    });
  };

  return (
    <Box>
      <PageTitle title="Transactions History" />
      <LoadingView loading={transactionsStatus === STATUS.LOADING} />

      <Sheet
        sx={{
          display: "flex",
          my: 1,
          gap: 1,
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ gap: 0.5, display: "flex" }}>
          <Button
            variant={transType === "deposits" ? "solid" : "outlined"}
            onClick={() => {
              setTransType("deposits");
              setCurrentPage(0);
            }}
            sx={{ borderRadius: 20 }}
          >
            Deposits
          </Button>
          <Button
            variant={transType === "withdrawals" ? "solid" : "outlined"}
            onClick={() => {
              setTransType("withdrawals");
              setCurrentPage(0);
            }}
            sx={{ borderRadius: 20 }}
          >
            Withdrawals
          </Button>
        </Box>

        {transData.length > 0 && (
          <Input
            size="sm"
            placeholder="Search transactions..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setCurrentPage(0);
            }}
            startDecorator={<SearchIcon />}
            sx={{ width: { xs: "auto", md: "30%" } }}
          />
        )}
      </Sheet>

      {transData.length > 0 ? (
        <>
          <MobileViewTable
            data={paginatedData}
            props={{ view: viewTransactionDetails }}
          />
          <DesktopViewTable
            data={paginatedData}
            props={{ view: viewTransactionDetails }}
          />

          {filteredData.length > ITEMS_PER_PAGE && (
            <Box
              sx={{
                pt: 2,
                gap: 1,
                [`& .${iconButtonClasses.root}`]: { borderRadius: "50%" },
                display: "flex",
              }}
            >
              <Button
                size="sm"
                variant="outlined"
                color="neutral"
                startDecorator={<KeyboardArrowLeftIcon />}
                onClick={() => setCurrentPage((p) => Math.max(0, p - 1))}
                disabled={currentPage === 0}
              >
                {isDesktop ? "Previous" : ""}
              </Button>

              <Box sx={{ flex: 1 }} />

              {Array.from({ length: pageLength }).map((_, page) => (
                <IconButton
                  key={page}
                  size="sm"
                  variant="outlined"
                  color="neutral"
                  onClick={() => setCurrentPage(page)}
                  disabled={currentPage === page}
                  sx={{ display: { xs: "none", md: "flex" } }}
                >
                  {page + 1}
                </IconButton>
              ))}

              <Typography
                level="body-sm"
                mx="auto"
                textAlign="center"
                sx={{ display: { xs: "flex", md: "none" } }}
              >
                Page {currentPage + 1} of {pageLength}
              </Typography>

              <Box sx={{ flex: 1 }} />

              <Button
                size="sm"
                variant="outlined"
                color="neutral"
                endDecorator={<KeyboardArrowRightIcon />}
                onClick={() =>
                  setCurrentPage((p) => Math.min(p + 1, pageLength - 1))
                }
                disabled={currentPage === pageLength - 1}
              >
                {isDesktop ? "Next" : ""}
              </Button>
            </Box>
          )}
        </>
      ) : (
        <Card
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            top: "20%",
          }}
        >
          <WarningRounded
            sx={{ fontSize: "50px", color: "background.appcolor" }}
          />
          <Typography level="title-md">Resource not Found</Typography>
          <Typography level="body-sm" textAlign="center">
            No transaction history found or a network issue occurred.
          </Typography>
          <Button
            onClick={() => dispatch(transactionsRequest(loginResult.token))}
            startDecorator={<RefreshOutlined />}
          >
            REFRESH
          </Button>
        </Card>
      )}
    </Box>
  );
};

const mapStateToProps = ({ auth }) => {
  const {
    otpResult: loginResult,
    transactionsStatus,
    transactionsResult,
    transactionsErrorMessage,
  } = auth;
  return {
    loginResult,
    transactionsStatus,
    transactionsResult,
    transactionsErrorMessage,
  };
};

export default connect(mapStateToProps, {})(TransactionPage);
